import { Box, FormControlLabel, Typography } from '@mui/material';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Checkbox,
  FullScreenLoader,
  MainSectionLayout,
} from '@Components/index';
import commonConstants from '@Constants/common.constants';
import { AccountSettingsTypes } from '@Containers/Settings/AccountSettings/AccountSettingsMain/AccountSettings.types';
import { AccountSettingsDto } from '@Models/settings.models';
import {
  useEditAccountSettingsMutation,
  useGetAccountSettingsQuery,
} from '@Services/settings.service';
import { AccountSettingTypes, BaseAlertProps } from '@Typings/global.types';
import useAlert from '@Utils/hooks/useAlert.hook';
import { useTokenAccIdValueHook } from '@Utils/hooks/useTokenValues.hook';

const AccountSettingsMain: FunctionComponent = (): JSX.Element => {
  const { t } = useTranslation();

  const headingText = t('accountSettings.Account Settings');
  const { triggerAlert } = useAlert();

  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [ringingSetting, setRingingSetting] = useState<AccountSettingsDto>({
    settingId: null,

    settingName: AccountSettingTypes.IS_RINGING_ENABLED,

    settingState: true,
  });

  const accountId = useTokenAccIdValueHook();

  // load all account settings
  const {
    data: dataOfAccountSettings,
    isLoading: loadingAccountSettings,
    isFetching: fetchingAccountSettings,
    isError: isErrorForGetAccountSettings,
    error: errorForGetAccountSettings,
  } = useGetAccountSettingsQuery(accountId ? accountId.toString() : '');

  // change account settings value by account id
  const [changeAccountSetting, changeAccountSettingMethods] =
    useEditAccountSettingsMutation();

  /**
   * Error and Success alerts for Account Settings Editing
   */
  useEffect(() => {
    if (
      changeAccountSettingMethods.isError &&
      changeAccountSettingMethods.error
    ) {
      triggerAlert(changeAccountSettingMethods.error, setAlertState);
    }
    if (changeAccountSettingMethods.isSuccess) {
      triggerAlert(
        commonConstants.GLOBAL_MESSAGES.ACCOUNT_SETTINGS_EDIT,
        setAlertState
      );
    }
  }, [
    changeAccountSettingMethods.isError,
    changeAccountSettingMethods.isSuccess,
    changeAccountSettingMethods.error,
    changeAccountSettingMethods.data,
    triggerAlert,
  ]);

  /**
   * Error Alerts for the Get account settings
   */
  useEffect(() => {
    if (isErrorForGetAccountSettings && errorForGetAccountSettings) {
      triggerAlert(errorForGetAccountSettings, setAlertState);
    }
  }, [triggerAlert, errorForGetAccountSettings, isErrorForGetAccountSettings]);

  const isLoading = loadingAccountSettings || fetchingAccountSettings;

  const handleRingingStateChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setRingingSetting({ ...ringingSetting, settingState: checked });
  };

  useEffect(() => {
    if (dataOfAccountSettings && dataOfAccountSettings.accountSettingsDto) {
      const ringingRecord = dataOfAccountSettings.accountSettingsDto.find(
        (accSetting) =>
          accSetting.settingName === AccountSettingTypes.IS_RINGING_ENABLED
      );
      if (ringingRecord)
        setRingingSetting({
          settingId: ringingRecord.settingId || null,
          settingName: ringingRecord.settingName,
          settingState: ringingRecord.settingState,
        });
    }
  }, [dataOfAccountSettings]);

  const accountRingingSettingsContainers = (
    <Box sx={{ p: 0 }}>
      <Typography
        variant="main"
        fontSize="16px"
        fontWeight="bold"
        textAlign="left"
        paddingLeft={0}
        sx={{ marginTop: 8, marginBottom: 1 }}
      >
        {t('accountSettings.Ringing Setting Name')}
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={ringingSetting.settingState}
            onChange={handleRingingStateChange}
            color="primary"
          />
        }
        label={t('accountSettings.Ringing Setting Description')}
        sx={{ marginLeft: -0.3 }}
      />
    </Box>
  );

  /** Form Submit Handler (Save Button Click) */
  const onSubmit: SubmitHandler<AccountSettingsTypes> = (formData) => {
    if (formData) {
      const settingsList = [];
      if (ringingSetting) {
        settingsList.push(ringingSetting);
      }
      return changeAccountSetting({
        accountId: accountId ? accountId.toString() : '',
        accountSettingsDto: settingsList,
      });
    }
  };

  const accountSettingsMethods = useForm<AccountSettingsTypes>({
    // resolver: yupResolver(AccountSettingsSchema()),
    mode: 'onChange',
    shouldUnregister: true,
  });

  const generalSettingsContainer = (
    <>
      <FormProvider {...accountSettingsMethods}>
        {!isLoading ? (
          <Box
            component="form"
            role="form"
            onSubmit={accountSettingsMethods.handleSubmit(onSubmit)}
          >
            {accountRingingSettingsContainers}
            <Box
              display="flex"
              flexDirection="column"
              sx={{
                paddingTop: '3rem',
              }}
            >
              <Box display="flex" justifyContent="flex-start" marginTop="2rem">
                <Button type="submit" color="primary">
                  {t('common.messages.Submit')}
                </Button>
              </Box>
            </Box>
          </Box>
        ) : (
          <FullScreenLoader />
        )}
      </FormProvider>
    </>
  );

  return (
    <>
      <MainSectionLayout
        heading={headingText}
        filters={generalSettingsContainer}
        alertState={alertState}
      />
    </>
  );
};

export default AccountSettingsMain;
